import type { MaterialInfo, PartDefinition } from "@rogue/rackbuilder"
import { getRackModel } from "../../../../modelPaths"
import { FunctionalTrainerBuilder } from "../functionalTrainerBuilder"
import { createFMLSideShrouds, createFMLShroudCutout, createTrolleyClip, createLatBarClip, createLowRowClip } from "./fmlSharedParts"
import { createFMLRollerPart } from "../FM/ftSharedParts"
import type { FMLContext } from "./fmlSharedParts"

// FMLBuilder — Abstract base for all RML-line Functional Trainer variants (FML-6, FML-HR, FML-Twin, FML-Twin-HR)
export abstract class FMLBuilder extends FunctionalTrainerBuilder {

  // ─── FM abstract method stubs ─────────────────────────────────────────────
  // FML uses a completely different assembly pattern and does not call these.
  // They exist only to satisfy the abstract contract of FunctionalTrainerBuilder.
  protected buildSideMeshes(materials: MaterialInfo, left: boolean): PartDefinition[] { return [] }
  protected buildFrontBottomPulley(materials: MaterialInfo, left: boolean): PartDefinition { return { mesh: '', slots: {} } }
  protected addResistanceSystem(_backUpright: PartDefinition, materials: MaterialInfo): void {}
  protected attachToHalfRack(sideMeshes: PartDefinition[], frontBottomPulley: PartDefinition, topCrossmember: PartDefinition, bottomCrossmember: PartDefinition, left: boolean): void {}
  protected attachToFullRack(sideMeshes: PartDefinition[], frontBottomPulley: PartDefinition, topCrossmember: PartDefinition, bottomCrossmember: PartDefinition, materials: MaterialInfo, left: boolean): void {}
  protected getModelPrefix(): string { return 'FML' }

  // ─── Context Factory ──────────────────────────────────────────────────────

  /**
   * Builds the shared FMLContext used by all part factories.
   * Subclasses call this directly — no need to override.
   */
  protected createContext(materials: MaterialInfo): FMLContext {
    return {
      config : this.config,
      materials,
      height : this.config.height,
      depth  : this.getDepthInInches(),
    }
  }

  // ─── Orchestration ────────────────────────────────────────────────────────

  protected addFunctionalTrainerComponents(part: PartDefinition): void {
    const materials = this.materialBuilder.createMainMaterial()
    const rightUpright = part.slots['other-side'] as PartDefinition

    this.addFMLSideComponents(part, materials, true)
    this.addFMLSideComponents(rightUpright, materials, false)
  }

  private addFMLSideComponents(
    frontUpright: PartDefinition,
    materials: MaterialInfo,
    left: boolean
  ): void {
    const bottomCrossmember = this.getBottomCrossmember(frontUpright)
    const depth = this.getDepthInInches()
    const isFullRack = !!this.config.sixPost

    if (isFullRack) {
      this.attachFMLFullRack(bottomCrossmember, materials, left, depth)
    } else {
      this.attachFMLHalfRack(bottomCrossmember, materials, left)
    }

    if (this.config.rackRoller && left) {
      const uprightForRoller = isFullRack
        ? (bottomCrossmember.slots.back as PartDefinition)
        : frontUpright

      uprightForRoller.slots['safety-low'] = createFMLRollerPart()
    }
  }

  private attachFMLFullRack(
    bottomCrossmember: PartDefinition,
    materials: MaterialInfo,
    left: boolean,
    depth: string
  ): void {
    const backUpright = bottomCrossmember.slots.back as PartDefinition
    const backCrossmember = backUpright.slots['crossmember-bottom'] as PartDefinition

    backCrossmember.slots.fml = this.buildFMLSideMeshes(materials, left, false)

    bottomCrossmember.slots.fml = [
      this.buildFMLCarriage(materials, left),
      this.buildFMLFrontSystem(materials, left, depth),
      ...this.buildFMLExtraFrontMeshes(materials, left, depth),
      this.buildFMLBackStabilizer(materials, left, depth, true),
    ]
  }

  private attachFMLHalfRack(
    bottomCrossmember: PartDefinition,
    materials: MaterialInfo,
    left: boolean
  ): void {
    const depth = this.getDepthInInches()

    bottomCrossmember.slots.fml = [
      this.buildFMLCarriage(materials, left),
      ...this.buildFMLSideMeshes(materials, left, true),
      this.buildFMLBackStabilizer(materials, left, depth, false),
      this.buildFMLHalfRackBody(materials, left),
      ...this.buildFMLHalfRackExtraMeshes(materials, left),
    ]
  }

  // ─── Shared FML parts ─────────────────────────────────────────────────────

  /**
   * Carriage with branding plate and cover — identical across Standard and Twin FML.
   */
  protected buildFMLCarriage(materials: MaterialInfo, left: boolean): PartDefinition {
    const trolleyClip = createTrolleyClip(left, this.config.trolleySelection)

    const brandingPlate: PartDefinition = {
      mesh        : getRackModel('FML', 'carriage branding plate'),
      slots       : {},
      anchor      : left ? '' : 'front',
      offset      : left ? {} : { z: 0.1 },
      adjustments : left ? [] : [{ type: 'flip', axis: 'Z' }],
    }

    const cover: PartDefinition = {
      mesh        : getRackModel('FML', 'carriage cover'),
      slots       : {},
      offset      : left ? {} : { x: 0.1 },
      adjustments : left ? [] : [{ type: 'flip', axis: 'X' }],
    }

    return {
      mesh        : getRackModel('FML', 'carriage'),
      adjustments : left ? [] : [{ type: 'flip', axis: 'X' }],
      materials,
      slots       : {
        trolley  : this.config.trolleySelection ? trolleyClip : [],
        branding : brandingPlate,
        cover,
      },
    }
  }

  /**
   * Back stabilizer shell — subclasses control the `middle` slot contents.
   */
  protected buildFMLBackStabilizer(
    materials: MaterialInfo,
    left: boolean,
    depth: string,
    isFullRack: boolean
  ): PartDefinition {
    const middleSlots = this.buildFMLBackStabilizerSlots(materials, left)

    return {
      mesh        : getRackModel('FML', 'back stabilizer'),
      slots       : middleSlots.length > 0 ? { middle: middleSlots } : {},
      anchor      : 'top',
      materials,
      adjustments : left ? [] : [{ type: 'flip', axis: 'X' }],
      offset      : isFullRack
        ? { y: 3, z: -Number(depth) - 22.5 }
        : { y: 3, z: -19.5 },
    }
  }

  /**
   * Low row attachment — used as part of the back stabilizer middle slot.
   */
  protected buildFMLLowRow(materials: MaterialInfo): PartDefinition {
    const lowRowClip = createLowRowClip(this.config.lowRowSelection)

    return {
      mesh   : getRackModel('FML', 'low row'),
      slots  : { low: this.config.lowRowSelection ? lowRowClip : [] },
      materials,
      offset : { x: 3 },
    }
  }

  /**
   * FML halfrack body — contains the `back shared` (with resistance on left side).
   * Used only in half rack assembly.
   */
  protected buildFMLHalfRackBody(materials: MaterialInfo, left: boolean): PartDefinition {
    return {
      mesh        : getRackModel('FML', 'halfrack'),
      materials,
      adjustments : left ? [] : [{ type: 'flip', axis: 'X' }],
      slots       : { back: this.buildFMLBackShared(materials, left) },
    }
  }

  /**
   * Back shared — resistance tower on left side, empty on right.
   * Used in half rack (inside halfrack body) and inside Standard FML's sharedRails.
   */
  protected buildFMLBackShared(materials: MaterialInfo, left: boolean): PartDefinition {
    return {
      mesh  : getRackModel('FML', 'back shared'),
      slots : left ? { resistance: this.buildFMLResistance(materials) } : {},
      materials,
    }
  }

  /**
   * Pullup bar with lat bar clip.
   * Standard: used inside topBrace → resistanceUnit.
   * Twin: used inside twinTopPulley → twinBackPost.
   */
  protected buildFMLPullup(): PartDefinition {
    const latBarClip = createLatBarClip(
      this.materialBuilder.createLatBarMaterial(),
      this.config.latBarSelection
    )

    return {
      mesh      : getRackModel('FML', 'pullup'),
      slots     : { lat: this.config.latBarSelection ? latBarClip : [] },
      anchor    : 'bolt',
      materials : { pulley: { material: 'stainless' } },
      offset    : { z: 2 },
    }
  }

  /**
   * Resistance tower — weight stack or plate carrier depending on config.edition.
   * Used by both Standard FML (in sharedRails) and half rack (in buildFMLBackShared).
   *
   * FML editions:
   *   'stack 250'           → short stack
   *   'stack 300' / 'twin'  → tall stack
   *   'plate loaded footplate' → plate carrier (rhino-style)
   */
  protected buildFMLResistance(materials: MaterialInfo): PartDefinition[] {
    const isPlateLoaded = this.config.edition === 'plate loaded footplate'
    const isStack250 = this.config.edition === 'stack 250'

    const pullup = this.buildFMLPullup()

    const topBrace: PartDefinition = {
      mesh   : getRackModel('FML', 'top brace'),
      slots  : { front: pullup },
      anchor : 'back',
      materials,
    }

    const resistanceUnit: PartDefinition = {
      mesh  : getRackModel('FML', isPlateLoaded ? 'plate carrier' : 'weight stack'),
      materials,
      slots : {
        back: topBrace,
        ...(!isPlateLoaded && {
          [`stack_${isStack250 ? 'short' : 'tall'}`]: {
            mesh  : getRackModel('FML', 'weight stack top'),
            slots : {},
            materials,
          },
        }),
      },
    }

    const resistance: PartDefinition[] = [resistanceUnit]

    if (this.config.ftShrouds && !isPlateLoaded) {
      resistance.push({
        mesh      : getRackModel('FML', 'stack shrouds'),
        materials : { shrouds: { material: 'matte' } },
        slots     : {},
      })
    }

    return resistance
  }

  /**
   * Extra meshes appended after the halfrack body in the half rack fml array.
   * Adds side shrouds when ftShrouds is enabled — identical for both Standard and Twin FML.
   * Subclasses may override if a variant needs different half rack extras.
   */
  protected buildFMLHalfRackExtraMeshes(
    materials: MaterialInfo,
    left: boolean
  ): PartDefinition[] {
    if (!this.config.ftShrouds) return []

    return [
      createFMLSideShrouds(left),
      createFMLShroudCutout(left),
    ]
  }

  // ─── FML Abstract Methods ─────────────────────────────────────────────────

  /**
   * Side meshes that attach to the back crossmember (full rack)
   * or inline in the fml slot array (half rack).
   *
   * Full rack Standard : [pulleys, ?shrouds, ?shroudCutout]
   * Full rack Twin     : [twinWeightStack, ?twinSideShrouds]
   * Half rack (both)   : [pulleys] — twin half rack reuses standard pulleys
   */
  protected abstract buildFMLSideMeshes(
    materials: MaterialInfo,
    left: boolean,
    isHalfRack: boolean
  ): PartDefinition[]

  /**
   * Front pulley/rail system — attaches to bottomCrossmember.slots.fml (full rack only).
   *
   * Standard : FML front + sharedRails (contains back shared + FML back mesh)
   * Twin     : FML_TWIN front crossmember pulleys (no back shared here)
   */
  protected abstract buildFMLFrontSystem(
    materials: MaterialInfo,
    left: boolean,
    depth: string
  ): PartDefinition

  /**
   * Extra parts after the front system in bottomCrossmember.slots.fml (full rack only).
   *
   * Standard : [] — nothing extra
   * Twin     : [twinTopRailPulley, twinBottomRailPulley]
   */
  protected abstract buildFMLExtraFrontMeshes(
    materials: MaterialInfo,
    left: boolean,
    depth: string
  ): PartDefinition[]

  /**
   * Contents of backStabilizer.slots.middle.
   * Right side always returns [] (backStabilizer has no middle on right).
   *
   * Standard left : [lowRow]
   * Twin left      : [twinBackPost, lowRow]
   * Both right     : []
   */
  protected abstract buildFMLBackStabilizerSlots(
    materials: MaterialInfo,
    left: boolean
  ): PartDefinition[]
}
